import { formatDateTime } from "@/lib/format";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Alert, Box, Button, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { notesApi } from "./api";
import { notesKeys } from "./queryKeys";

const MAX = 300;

export function NotesPanel() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const list = useQuery({ queryKey: notesKeys.lists(), queryFn: notesApi.list });

  const createM = useMutation({
    mutationFn: (text: string) => notesApi.create(text),
    onSuccess: () => {
      setDraft("");
      void qc.invalidateQueries({ queryKey: notesKeys.lists() });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const removeM = useMutation({
    mutationFn: notesApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: notesKeys.lists() }),
  });

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Typography variant="h6" gutterBottom>
        Notes
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 2 },
          alignItems: { xs: "stretch", sm: "flex-start" },
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={2}
          required
          label="Enter a note"
          value={draft}
          onChange={(e) => {
            setErr(null);
            setDraft(e.target.value.slice(0, MAX));
          }}
          helperText={`${draft.length} / ${MAX}`}
        />
        <Button
          variant="contained"
          disabled={!draft.trim() || createM.isPending}
          onClick={() => createM.mutate(draft.trim())}
        >
          Create
        </Button>
      </Box>
      {err && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {err}
        </Alert>
      )}
      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />} sx={{ mt: 2 }}>
        {(list.data ?? []).map((n) => (
          <Box
            key={n.id}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              py: 1,
              gap: { xs: 0.25, sm: 2 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "row", sm: "row" },
                gap: { xs: 1, sm: 2 },
                flexWrap: "wrap",
                alignItems: "center",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography
                variant="body2"
                sx={{ minWidth: { sm: 200 }, color: "text.secondary" }}
              >
                {formatDateTime(n.createdAt)}
              </Typography>
              <Typography variant="body2" sx={{ minWidth: { sm: 160 }, fontWeight: 600 }}>
                {n.userInfo ? `${n.userInfo.firstName} ${n.userInfo.lastName}:` : "—"}
              </Typography>
            </Box>
            <Typography
              sx={{ flex: 1, width: { xs: "100%", sm: "auto" }, wordBreak: "break-word" }}
            >
              {n.text}
            </Typography>
            <IconButton
              color="error"
              onClick={() => removeM.mutate(n.id)}
              sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        {!list.isLoading && (list.data ?? []).length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No notes yet.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
